import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, take } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
    BuildProgrammeTemplateLogicService,
    IBuildProgrammeTemplateDto,
    IBuildTemplateActivityDto,
    IBuildTemplateActivityRelationshipDto,
    IBuildTemplateStageDto
} from '@app/logic/build-programme-template';
import { NavigationService } from '@app/core/services/navigation/navigation.service';
import { filter, find, flatten, includes, map, some } from 'lodash';
import { CurrentUserService } from '@app/core/authentication/current.user';
import { BuildProgrammeTemplatePermissions } from '@app/core/permissions';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import {
    BuildProgrammeTemplateActivityDialogComponent
} from '../build-programme-template-activity-dialog/build-programme-template-activity-dialog.component';
import { BuildStagesLogicService, IBuildStageDto } from '@app/logic/build-stages';
import { BuildActivitiesLogicService } from '@app/logic/build-activities';
import { ISkinnyBuildActivityDto } from '@classictechsolutions/hubapi-transpiled-enums';
import { HISTORY_SEARCH_ENTITY } from '@app/shared/enums/history/history-search-entity.enum';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

enum TabNames {
    Template = 'template',
    History = 'history',
}

enum Tabs {
    Template,
    History,
}

enum DropList {
    AvailableStages = 'available-stages-drop-list',
    Activities = 'activities-drop-list-',
    AvailableActivities = 'available-activities-drop-list'
}

@Component({
    selector: 'cb-build-programme-template-view',
    templateUrl: './build-programme-template-view.component.html',
    styleUrls: ['./build-programme-template-view.component.scss'],
    standalone: false
})
export class BuildProgrammeTemplateViewComponent implements OnInit {
    public templateId: number;
    public currentTabIndex = Tabs.Template;
    public template: IBuildProgrammeTemplateDto;
    public isEditing$ = new BehaviorSubject<boolean>(undefined);
    public refresh$ = new BehaviorSubject<boolean>(undefined);
    public expansionPanelStates: { [key: number]: boolean } = {};
    public buildStages: IBuildStageDto[];
    public availableStages: IBuildStageDto[];
    public buildActivities: ISkinnyBuildActivityDto[];
    public availableActivities: ISkinnyBuildActivityDto[];
    public activityFilter: string;

    public readonly activityColumns = ['', 'Code', 'Name', 'Duration', ''];
    public readonly DropList = DropList;
    public readonly denyDropIntoSource = () => false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly navigationService: NavigationService,
        private readonly currentUserService: CurrentUserService,
        private readonly cbDialog: CbDialogService,
        private readonly buildProgrammeTemplatePermissions: BuildProgrammeTemplatePermissions,
        private readonly buildProgrammeTemplateLogic: BuildProgrammeTemplateLogicService,
        private readonly buildStagesLogic: BuildStagesLogicService,
        private readonly buildActivitiesLogic: BuildActivitiesLogicService,
    ) {
        this.isEditing$.subscribe((isEditing: boolean) => {
            if (isEditing === false) {
                this.refresh();
            }
            this.setAvailableStages();
            this.setAvailableActivities();
        });
        this.refresh$.subscribe(() => this.refresh());
        this.buildStagesLogic.getAllNonPreConsentStageBuildStages().pipe(take(1)).subscribe(result => this.buildStages = result ?? []);
        this.buildActivitiesLogic.getSkinnyList().pipe(take(1)).subscribe(result => this.buildActivities = result ?? []);
    }

    public ngOnInit(): void {
        this.route.params.pipe(take(1)).subscribe((params: { [key: string]: string }) => {
            this.templateId = params.id as unknown as number;
            this.currentTabIndex = params.tabName === TabNames.Template ? Tabs.Template : Tabs.History;
            this.refresh();
        });
    }

    public dropStage(event: CdkDragDrop<IBuildTemplateStageDto[] | IBuildStageDto[]>): void {
        // reordering stages in the left list
        if (event.previousContainer === event.container) {
            moveItemInArray(this.template.stages, event.previousIndex, event.currentIndex);
            return;
        }
        // a new stage was added from the right list (tab "Stages").
        if (event.previousContainer.id === DropList.AvailableStages) {
            const stage = this.availableStages[event.previousIndex];
            const newStage: IBuildTemplateStageDto = {
                buildStageId: stage.id,
                buildStageCode: stage.code,
                buildStageLabel: stage.label,
                activities: [],
                sortOrder: event.currentIndex
            } as IBuildTemplateStageDto;
            this.template.stages.splice(event.currentIndex, 0, newStage);
            // removal of a stage from the source and update
            this.availableStages.splice(event.previousIndex, 1);
            this.setAvailableStages();
            return;
        }
    }

    public get activityTargets(): string[] {
        return this.template?.stages?.map(s => DropList.Activities + s.buildStageId) ?? [];
    }

    public get stageActivitiesConnected(): string[] {
        const ids = this.template?.stages?.map(s => DropList.Activities + s.buildStageId) ?? [];
        ids.push(DropList.AvailableActivities);
        return ids;
    }

    public dropActivity(
        targetStage: IBuildTemplateStageDto,
        event: CdkDragDrop<IBuildTemplateActivityDto[]>
    ): void {
        // reordering activity in the left list
        if (event.previousContainer === event.container) {
            moveItemInArray(targetStage.activities, event.previousIndex, event.currentIndex);
        } else {
            // a new activity was added (left list)
            const sourceStage = this.template.stages.find(
                stage => stage.activities === event.previousContainer.data
            );
            if (sourceStage) {
                const draggedActivity = event.previousContainer.data[event.previousIndex];
                // removal of an activity from the source (left list) and update
                sourceStage.activities.splice(event.previousIndex, 1);
                targetStage.activities.splice(event.currentIndex, 0, {
                    ...draggedActivity,
                    sortOrder: event.currentIndex
                });
            } else if (event.previousContainer.id === DropList.AvailableActivities) {
                // a new stage was added from the right list (tab "Activities")
                const activity = this.availableActivities[event.previousIndex];

                const newItem = ({
                    activity,
                    sortOrder: event.currentIndex
                } as unknown) as IBuildTemplateActivityDto;

                targetStage.activities.splice(event.currentIndex, 0, newItem);

                this.availableActivities.splice(event.previousIndex, 1);
                this.setAvailableActivities();
            }

            this.template.stages.forEach(stage => {
                stage.activities.forEach((activity, index) => (activity.sortOrder = index));
            });
        }
    }

    public tabIndexChanged(tabIndex: Tabs): void {
        const tabName = tabIndex === Tabs.Template ? TabNames.Template : TabNames.History;
        this.navigationService.navigate([`/build-programme-templates/view/${this.templateId}/${tabName}`]);
    }

    public refresh(): void {
        if (this.templateId) {
            this.buildProgrammeTemplateLogic.$getItem(this.templateId).pipe(take(1)).subscribe((result: IBuildProgrammeTemplateDto) => this.template = result);
        }
    }

    public expandOrCollapseAll(): void {
        if (this.template.stages?.length) {
            if (this.isAllExpanded()) {
                this.template.stages.forEach(stage => this.expansionPanelStates[stage.buildStageId] = false);
            } else {
                this.template.stages.forEach(stage => this.expansionPanelStates[stage.buildStageId] = true);
            }
        }
    }

    public isAllExpanded(): boolean {
        return this.template?.stages?.length ? !find(this.template?.stages, stage => !this.expansionPanelStates[stage.buildStageId]) : false;
    }

    public canEdit(): boolean {
        let canEdit = false;
        if (this.buildProgrammeTemplatePermissions.canEditAll()) {
            canEdit = true;
        } else if (this.template) {
            canEdit = this.template.regions.some(i => {
                return includes(this.currentUserService.regionsids ?? [], i) && this.buildProgrammeTemplatePermissions.canEdit();
            });
        }
        return canEdit;
    }

    public canRemoveActivity(activity: IBuildTemplateActivityDto): boolean {
        if (activity.hasDependencies === undefined) {
            activity.hasDependencies = (this.template && find(this.template.stages, (stage) => {
                return find(stage.activities, (stageActivity: IBuildTemplateActivityDto) => {
                    return find(stageActivity.relationships, (dependency: IBuildTemplateActivityRelationshipDto) => {
                        return dependency.predecessorBuildProgrammeActivityId === activity.id;
                    });
                });
            })) !== undefined;
        }

        return !activity.hasDependencies;
    }

    public activityEditClicked(stage: IBuildTemplateStageDto, activity: IBuildTemplateActivityDto): void {
        const dialog = this.cbDialog.open(BuildProgrammeTemplateActivityDialogComponent, {
            data: {
                dialogHeading: 'Edit Activity',
                template: this.template,
                stage,
                activity,
                refresh$: this.refresh$,
            }
        });
        dialog.afterClosed().pipe(take(1)).subscribe(() => this.refresh());
    }

    public removeStageClicked(stage: IBuildTemplateStageDto): void {
        this.template.stages = filter(this.template.stages, (item: IBuildTemplateStageDto) => item.buildStageId !== stage.buildStageId);
        this.setAvailableStages();
    }

    public removeActivityClicked(stage: IBuildTemplateStageDto, activity: IBuildTemplateActivityDto): void {
        stage.activities = filter(stage.activities, (item: IBuildTemplateActivityDto) => item.activity.id !== activity.activity.id);
        this.setAvailableActivities();
    }

    public saveClicked(): void {
        const stages = map(this.template.stages, (stage: IBuildTemplateStageDto) => {
            return {
                buildStageId: stage.buildStageId,
                activities: stage.activities ?? []
            } as IBuildTemplateStageDto;
        });
        if (stages.length) {
            for (let i = 0; i < stages.length; i++) {
                stages[i].sortOrder = i;
                if (stages[i].activities?.length) {
                    for (let j = 0; j < stages[i].activities.length; j++) {
                        stages[i].activities[j].sortOrder = j;
                    }
                }
            }
        }
        this.buildProgrammeTemplateLogic.saveStages(this.templateId, stages).pipe(take(1)).subscribe(() => this.isEditing$.next(false));
    }

    public setAvailableActivities(): void {
        const existingActivities = flatten(map(this.template?.stages ?? [], (stage: IBuildTemplateStageDto) => stage.activities ?? []));
        this.availableActivities = this.buildActivities
            ? filter(this.buildActivities.slice(), (activity: ISkinnyBuildActivityDto) => !some(existingActivities,
                (item: IBuildTemplateActivityDto) => item.activity.id === activity.id)
                && (this.activityFilter ? activity.code.toLowerCase().includes(this.activityFilter) || activity.name.toLowerCase().includes(this.activityFilter) : true))
            : [];
    }

    private setAvailableStages(): void {
        this.availableStages = this.buildStages && this.template?.stages
            ? filter(this.buildStages.slice(), (stage: IBuildStageDto) => !some(this.template.stages, { buildStageId: stage.id }))
            : [];
    }

    protected readonly HISTORY_SEARCH_ENTITY = HISTORY_SEARCH_ENTITY;
}
